import { KlevuSearchSorting } from "@klevu/core";
import { useLocation } from "@reach/router";
import { useWindowSize } from "@react-hookz/web";
import { navigate } from "gatsby";
import { debounce } from "lodash";
import qs from "query-string";
import {
	useCallback,
	useEffect,
	useMemo,
	useState,
	type SetStateAction,
} from "react";
import {
	applyCMSSearch,
	applySearch,
	applyTrendingProducts,
	type PartialFilter,
} from "../../utils/klevu/search";
import { useDebounce } from "../use-debounce";
import {
	FilterType,
	type Filter,
	type SearchConfiguration,
	type SearchResultCMS,
	type SearchResultProduct,
	type SelectedFilter,
	type UseSearch,
	type UseSearchParams,
} from "./types";

type SetPage = React.Dispatch<SetStateAction<number>>;

const incrementPage = (setPage: SetPage) => {
	setPage((prevPage) => prevPage + 1);
};

// Get the limit of products/pages to show based on the current width
const getLimit = (
	currentWidth: number,
	breakpoints: Record<number, number>,
): number => {
	const closestWidth = Object.keys(breakpoints)
		.map(Number)
		.findLast((breakpoint) => breakpoint < currentWidth);
	// default to 2 if no minWidth breakpoint is found (mobile-first approach)
	return closestWidth ? (breakpoints[closestWidth] as number) : 2;
};

const mergeFilters = (
	filters: Filter[],
	selectedFilters: PartialFilter[],
): Filter[] => {
	return filters.map((filter) => {
		const selectedFilter = selectedFilters.find(
			(selectedFilter) => filter.key === selectedFilter.key,
		);

		if (selectedFilter) {
			for (const selectedFilterOption of selectedFilter.options) {
				const filterOption = filter.options.find(
					(option) => option.value === selectedFilterOption.value,
				);

				if (filterOption) {
					filterOption.selected = selectedFilterOption.selected;
				}
			}
		}
		return filter;
	});
};

const getPartialFiltersFromQuery = (
	query: qs.ParsedQuery<string>,
): PartialFilter[] => {
	const filters = Object.keys(query).filter((key) => key.startsWith("f_"));

	return filters.map((key) => {
		const options = Array.isArray(query[key])
			? query[key].map((value) => ({
					value: value === null ? "" : value,
					selected: true,
				}))
			: [{ value: query[key] === null ? "" : query[key], selected: true }];

		return {
			key: key.replace("f_", ""),
			options,
		};
	});
};

const debouncedHistoryReplace = debounce((url: string) => {
	history.replaceState(null, "", url);
}, 500);

const debouncedNavigate = debounce((url: string) => {
	void navigate(url, { replace: true });
}, 500);

export const useSearch = (props: UseSearchParams): UseSearch => {
	const [searchTerm, setSearchTerm] = useState(props.searchTerm ?? "");
	const location = useLocation();
	const searchQuery = qs.parse(location.search, {
		arrayFormat: "separator",
		arrayFormatSeparator: "|",
	});

	const [sortTerm, setSortTerm] = useState<KlevuSearchSorting>(
		KlevuSearchSorting.Relevance,
	);
	const [productResults, updateSearchProductResults] = useState<
		SearchResultProduct[]
	>([]);
	const [cmsResults, updateCmsResults] = useState<SearchResultCMS[]>([]);
	const [suggestionResults, setSuggestionResults] = useState<
		{ suggest: string }[]
	>([]);
	const [filters, setFilters] = useState<Filter[]>([]);

	const [selectedFilters, setSelectedFilters] = useState<PartialFilter[]>(
		getPartialFiltersFromQuery(searchQuery),
	);
	const [currentProductsPage, setCurrentProductsPage] = useState(1);
	const [currentCMSPage, setCurrentCMSPage] = useState(1);
	const [loading, setLoading] = useState(!!searchTerm);
	const [error, setError] = useState(false);
	const [hasNextPage, setHasNextPage] = useState<boolean>(false);

	const debouncedValue = useDebounce(searchTerm, 500);
	const { width } = useWindowSize();
	const showMoreProducts = () => incrementPage(setCurrentProductsPage);
	const showMorePages = () => incrementPage(setCurrentCMSPage);

	// The key is the minimum width and the value is the number of products to show
	const productsBreakpoints = {
		363: 4,
		582: 6,
		768: 8,
		929: 10,
		991: 8,
		1244: 10,
		1496: 12,
		1748: 14,
		2001: 16,
		2253: 18,
		2506: 20,
		2758: 22,
		3010: 24,
		3263: 26,
	};

	// The key is the minimum width and the value is the number of pages to show
	const pagesBreakpoints = {
		643: 4,
		929: 6,
		991: 4,
		1127: 6,
		1515: 8,
		1885: 10,
		2291: 12,
		2679: 14,
		3066: 16,
	};

	const productsLimit =
		props.limit === undefined
			? getLimit(width, productsBreakpoints)
			: props.limit;

	const pagesLimit =
		props.limit === undefined ? getLimit(width, pagesBreakpoints) : props.limit;

	const updateSelectedFilters = useMemo(
		() =>
			(selectedFilter: SelectedFilter): PartialFilter[] => {
				const existingFilter = selectedFilters.find(
					(x) => x.key === selectedFilter.key,
				);

				let allFilters = [];

				if (existingFilter) {
					const filterOption = existingFilter.options.find(
						(option) => option.value === selectedFilter.option.value,
					);
					if (filterOption) {
						filterOption.selected = selectedFilter.option.selected;
					} else {
						existingFilter.options.push(selectedFilter.option);
					}
					const newFilters = [
						...selectedFilters.filter((x) => x.key !== selectedFilter.key),
						existingFilter,
					];
					allFilters = newFilters;
				} else {
					const newFilter: Filter = {
						key: selectedFilter.key,
						label: selectedFilter.label,
						translatedLabel: "",
						options: [],
						type: FilterType.dynamic,
					};
					newFilter.options.push(selectedFilter.option);
					allFilters = [...selectedFilters, newFilter];
				}

				return allFilters;
			},
		[selectedFilters],
	);

	useEffect(() => {
		const currentUrl = qs.parseUrl(location.href, {
			parseFragmentIdentifier: true,
		});

		for (const filter of selectedFilters) {
			if (filter.options.length > 0) {
				const selectedOptions = filter.options.filter(
					(option) => option.selected,
				);
				if (selectedOptions.length > 0) {
					currentUrl.query[`f_${filter.key}`] = selectedOptions
						.map((option) => option.value ?? "")
						.join("|");
				} else {
					currentUrl.query[`f_${filter.key}`] = null;
				}
			}
		}

		// Only update URL with query string on search page
		const pathname = location.pathname;
		const isSearchPage = pathname.includes("/search");

		if (isSearchPage) {
			debouncedHistoryReplace(qs.stringifyUrl(currentUrl));
		}
	}, [location.href, selectedFilters, location.pathname]);

	const search = useCallback(
		(
			searchTerm: string,
			selectedFilter: SelectedFilter | undefined,
			_configuration: SearchConfiguration,
			sortTerm?: KlevuSearchSorting,
		) => {
			setLoading(true);
			setSearchTerm(searchTerm);
			if (sortTerm) {
				setSortTerm(sortTerm);
			}
			if (selectedFilter) {
				setSelectedFilters(updateSelectedFilters(selectedFilter));
			}
			setCurrentProductsPage(1);
			setCurrentCMSPage(1);
			setLoading(false);
			setError(false);
		},
		[updateSelectedFilters],
	);

	const clear = useCallback(() => {
		setLoading(true);
		setSearchTerm("");
		setSortTerm(KlevuSearchSorting.Relevance);
		setSuggestionResults([]);
		setError(false);
		void (async () => {
			if (
				(productResults.length === 0 && debouncedValue !== "") ||
				selectedFilters.length > 0
			) {
				const result = await applyTrendingProducts(
					undefined,
					1,
					3,
					KlevuSearchSorting.Relevance,
				);
				updateSearchProductResults(result.results);
			}
			if (selectedFilters.length > 0) {
				setSelectedFilters([]);
			}

			const cmsResult = await applyCMSSearch("", 1, 3);
			updateCmsResults(cmsResult.results);

			if (currentProductsPage > 1) {
				setCurrentProductsPage(1);
			}

			if (currentCMSPage > 1) {
				setCurrentCMSPage(1);
			}

			// Only update URL with query string on search page
			const pathname = location.pathname;
			const isSearchPage = pathname.includes("/search");

			if (isSearchPage) {
				debouncedNavigate("?");
			}
			setLoading(false);
		})();
	}, [
		currentProductsPage,
		currentCMSPage,
		debouncedValue,
		productResults.length,
		selectedFilters.length,
		location.pathname,
	]);

	const removeFilter = useCallback(
		(filterToRemove: Filter) => {
			if (loading) return;
			setLoading(true);
			setError(false);

			void (() => {
				const filter = filters.find(
					(filter) => filter.key === filterToRemove.key,
				);

				if (filter) {
					for (const option of filter.options) {
						if (option.selected) {
							setSelectedFilters(
								updateSelectedFilters({
									...filter,
									option: { ...option, selected: false },
								}),
							);
						}
					}
				}

				setLoading(false);
			})();
		},
		[loading, filters, updateSelectedFilters],
	);

	const fetchSearchResults = useCallback(async () => {
		setLoading(true);
		setError(false);
		try {
			const result = debouncedValue
				? await applySearch(
						debouncedValue,
						selectedFilters,
						currentProductsPage,
						productsLimit,
						sortTerm,
					)
				: await applyTrendingProducts(
						selectedFilters,
						currentProductsPage,
						productsLimit,
						sortTerm,
					);

			setHasNextPage(result.hasNextPage);
			setSuggestionResults(result.suggestions);

			if (currentProductsPage === 1) {
				updateSearchProductResults(result.results);
			} else {
				updateSearchProductResults((prevResults) => [
					...prevResults,
					...result.results,
				]);
			}

			setFilters(result.filters);
		} catch (error_) {
			setError(true);
			console.error("Error fetching data:", error_);
		} finally {
			setLoading(false);
		}
	}, [
		currentProductsPage,
		debouncedValue,
		selectedFilters,
		productsLimit,
		sortTerm,
	]);

	const fetchCMSData = useCallback(async () => {
		setLoading(true);
		setError(false);
		try {
			const cmsResult = await applyCMSSearch(
				debouncedValue,
				currentCMSPage,
				pagesLimit,
			);
			setHasNextPage(cmsResult.hasNextPage);
			if (currentCMSPage === 1) {
				updateCmsResults(cmsResult.results);
			} else {
				updateCmsResults((prevResults) => [
					...prevResults,
					...cmsResult.results,
				]);
			}
		} catch (error_) {
			setError(true);
			console.error("Error fetching CMS data:", error_);
		} finally {
			setLoading(false);
		}
	}, [currentCMSPage, debouncedValue, pagesLimit]);

	useEffect(() => {
		if (props.searchType === "product") {
			void fetchSearchResults();
		} else {
			void fetchCMSData();
		}
	}, [fetchCMSData, fetchSearchResults, props.searchType]);

	useEffect(() => {
		const currentQuery = qs.parse(location.search);
		const newQuery = { ...currentQuery, q: debouncedValue || undefined };

		// Only update URL with query string on search page
		const pathname = location.pathname;
		const isSearchPage = pathname.includes("/search");

		if (isSearchPage) {
			debouncedNavigate(`?${qs.stringify(newQuery)}`);
		}
	}, [debouncedValue, location.search, location.pathname]);

	return {
		search,
		showMoreProducts,
		showMorePages,
		clear,
		removeFilter,
		productResults,
		suggestionResults,
		cmsResults,
		filters: mergeFilters(filters, selectedFilters),
		loading,
		error,
		hasNextPage,
		sortTerm,
		query: searchTerm,
	};
};
